import { useNavigate } from "react-router-dom";
import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import Skeleton from "react-loading-skeleton";
import ConsolidatedSummary, { parseConsolidatedSummary } from "./ConsolidatedSummary";
import "../Relatedtopics.css";
import { createMyNote, getMyNotesSubjects } from "../services/myNotesService";
import { createCurrentAffairsSelectionNoteDraft } from "../utils/myNotes";
import EditNoteModal from "./MyNotes/EditNoteModal";
import NoteToast from "./MyNotes/NoteToast";
import html2pdf from "html2pdf.js";
import watermarkImage from "../assets/images/logo/SVG/Logo-Org.svg";
import pdfHeaderLogo from "../assets/images/pdfHeaderLogo.svg";
import { decoratePdf } from "../utils/pdfDecoration";
const RelatedTopics = ({
  id,
  relatedTopicsData,
  relatedLoader,
  relatedSummariesData,
  relatedSummariesLoader,
  showpracticetest,
  onStartTest,
  keywords,
  setTopicKeyword,
  showOnlySummaries,
  syllabusProgressData,
  syllabusProgressLoading
}) => {
  const navigate = useNavigate();
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [editNoteDraft, setEditNoteDraft] = useState(null);
  const [showEditNoteModal, setShowEditNoteModal] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteSubjects, setNoteSubjects] = useState([]);
  const [noteToastMessage, setNoteToastMessage] = useState("");
  const [noteToastVariant, setNoteToastVariant] = useState("success");
  const [selectionPopup, setSelectionPopup] = useState({
    visible: false,
    x: 0,
    y: 0,
    text: "",
  });


  const formatSummaryDate = (dateValue) => {
    if (!dateValue) {
      return "";
    }

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return dateValue;
    }

    const month = date.toLocaleDateString("en-US", { month: "short" });
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const boldNumbers = (text) => {
    if (!text) return text;
    return text.split(/(\d+)/g).map((part, i) =>
      /^\d+$/.test(part) ? <strong key={i}>{part}</strong> : part
    );
  };

  const stripHtml = (value) => {
    if (!value || typeof value !== "string") {
      return "";
    }

    return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  };

  const extractBulletLines = (item, limit = 3) => {
    const bulletCandidates = [
      item?.bullets,
      item?.bullet_points,
      item?.bulletPoints,
      item?.key_points,
      item?.keyPoints,
      item?.highlights,
    ];

    const explicitBullets = bulletCandidates.find((candidate) => {
      if (Array.isArray(candidate) && candidate.length > 0) {
        return true;
      }

      if (typeof candidate === "string" && candidate.trim()) {
        return true;
      }

      return false;
    });

    if (Array.isArray(explicitBullets)) {
      return explicitBullets
        .map((entry) => stripHtml(typeof entry === "string" ? entry : entry?.text || entry?.title || ""))
        .filter(Boolean)
        .slice(0, limit);
    }

    if (typeof explicitBullets === "string") {
      return explicitBullets
        .split(/\r?\n|•|-/)
        .map((entry) => stripHtml(entry))
        .filter((entry) => entry.length > 12)
        .slice(0, limit);
    }

    const fallbackText = [item?.summary, item?.description, item?.content]
      .map((entry) => stripHtml(entry))
      .filter(Boolean)
      .join(" ");

    return fallbackText
      .split(/(?<=[.!?])\s+/)
      .map((entry) => entry.trim())
      .filter(Boolean)
      .slice(0, limit);
  };

  const consolidatedItem = relatedSummariesData?.find(
    (item) => item?.isConsolidated || (item?.summary && item?.articles_used),
  );

  const handleShareSummary = async () => {
    const shareText = consolidatedItem?.summary
      || relatedSummariesData
        ?.map((item) => item.title || item.topic || item.name)
        .filter(Boolean)
        .join("\n");

    if (!shareText) {
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Current Affairs Summary",
          text: shareText,
        });
        return;
      } catch (error) {
        // Fall back to clipboard when native share is cancelled/unavailable.
      }
    }

    try {
      await navigator.clipboard.writeText(shareText);
    } catch (error) {
      console.error("Failed to copy summary share text", error);
    }
  };

  const escapeHtmlEntities = (value = "") =>
    String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const formatInlineMd = (text) =>
    escapeHtmlEntities(text).replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

  const buildSummaryTitle = () => {
    if (!consolidatedItem) {
      return "Current Affairs Summary";
    }
    return (
      parseConsolidatedSummary(
        consolidatedItem.summary || consolidatedItem.content,
      ).title ||
      consolidatedItem.title ||
      consolidatedItem.topic ||
      "Current Affairs Summary"
    );
  };

  const buildSummaryHtml = () => {
    if (consolidatedItem?.summary) {
      const parsed = parseConsolidatedSummary(
        consolidatedItem.summary || consolidatedItem.content,
      );
      const refs =
        consolidatedItem.articles_used && consolidatedItem.articles_used.length > 0
          ? consolidatedItem.articles_used.map((a) => ({ label: a.topic }))
          : parsed.references;

      let html = "";
      if (parsed.gsTags.length > 0) {
        html += `<p>${parsed.gsTags.map((t) => escapeHtmlEntities(t)).join(", ")}</p>`;
      }
      parsed.sections.forEach((section) => {
        html += `<h3 class="h3_my-notes-modal-body">${escapeHtmlEntities(section.heading)}</h3><ul>`;
        section.items.forEach((item) => {
          html += `<li>${formatInlineMd(item)}</li>`;
        });
        html += `</ul>`;
      });
      if (refs.length > 0) {
        html += `<h3 class="h3_my-notes-modal-body">Yooki References</h3><ul>`;
        refs.forEach((ref) => {
          html += `<li>${escapeHtmlEntities(ref.label)}</li>`;
        });
        html += `</ul>`;
      }
      return html;
    }

    return (relatedSummariesData || [])
      .map((item) => {
        const title = item.title || item.topic || item.name || "";
        const bullets = extractBulletLines(item, 20)
          .map((point) => `<li>${escapeHtmlEntities(point)}</li>`)
          .join("");
        return `${title ? `<h3 class="h3_my-notes-modal-body">${escapeHtmlEntities(title)}</h3>` : ""}<ul>${bullets}</ul>`;
      })
      .filter(Boolean)
      .join("");
  };

  const handleDownloadSummaryPdf = () => {
    const contentHtml = buildSummaryHtml();
    if (!contentHtml) {
      return;
    }

    const wrapper = document.createElement("div");
    wrapper.style.position = "relative";

    const titleDiv = document.createElement("div");
    titleDiv.style.fontWeight = "bold";
    titleDiv.style.marginBottom = "20px";
    titleDiv.style.fontSize = "18px";
    titleDiv.textContent = buildSummaryTitle();
    wrapper.appendChild(titleDiv);

    const contentDiv = document.createElement("div");
    contentDiv.innerHTML = contentHtml;
    wrapper.appendChild(contentDiv);

    const opt = {
      margin: [0.5, 0.3, 0.6, 0.3],
      filename: "current-affairs-summary.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
      },
      jsPDF: {
        unit: "in",
        format: "letter",
        orientation: "portrait",
      },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    document.body.appendChild(wrapper);

    html2pdf()
      .set(opt)
      .from(wrapper)
      .toPdf()
      .get("pdf")
      .then(async (pdf) => {
        const totalPages = pdf.internal.getNumberOfPages();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        await decoratePdf({
          pdf,
          headerLogo: pdfHeaderLogo,
          watermark: watermarkImage,
          totalPages,
          pageWidth,
          pageHeight,
        });

        try {
          document.body.removeChild(wrapper);
        } catch (e) {
          // Already removed
        }

        pdf.save(opt.filename);
      })
      .catch((err) => {
        console.error("PDF generation failed:", err);

        try {
          if (document.body.contains(wrapper)) {
            document.body.removeChild(wrapper);
          }
        } catch (e) {
          // Already removed
        }

        alert("Error generating PDF. Please try again.");
      });
  };

  const handleSaveSummaryToNotes = () => {
    const contentHtml = buildSummaryHtml();
    if (!contentHtml) {
      return;
    }

    const noteDraft = {
      title: buildSummaryTitle(),
      content: `<div>${contentHtml}</div>`,
      tags: ["Current Affairs"],
      attachments: [],
    };

    setEditNoteDraft(noteDraft);
    setShowEditNoteModal(true);
  };

  const handleEditNoteSave = async (noteData) => {
    setIsSavingNote(true);
    try {
      await createMyNote(noteData);
      setShowEditNoteModal(false);
      setEditNoteDraft(null);
      setNoteToastVariant("success");
      setNoteToastMessage("Your note was saved successfully.");
    } catch (noteError) {
      console.error("Failed to create summary note:", noteError);
      setNoteToastVariant("error");
      setNoteToastMessage("We could not save your note. Please try again.");
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleEditNoteClose = () => {
    setShowEditNoteModal(false);
    setEditNoteDraft(null);
  };

  const clampPopupToViewport = (x, y) => {
    const margin = 12;
    const safeX = Math.min(Math.max(x, margin), window.innerWidth - margin);
    const safeY = Math.min(Math.max(y, margin), window.innerHeight - margin);
    return { x: safeX, y: safeY };
  };

  const handleModalTextSelect = (e) => {
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim() || "";

    const getSelectionEndRect = () => {
      if (!selection?.rangeCount || !selection.focusNode) {
        return null;
      }

      try {
        const focusRange = document.createRange();
        const focusNode = selection.focusNode;
        const maxOffset =
          focusNode.nodeType === Node.TEXT_NODE
            ? focusNode.textContent?.length || 0
            : focusNode.childNodes?.length || 0;
        const safeOffset = Math.min(selection.focusOffset || 0, maxOffset);

        focusRange.setStart(focusNode, safeOffset);
        focusRange.setEnd(focusNode, safeOffset);

        const focusRect = focusRange.getBoundingClientRect();
        if (focusRect && (focusRect.width || focusRect.height)) {
          return focusRect;
        }
      } catch (error) {
        // Fallback to collapsed range end below.
      }

      const range = selection.getRangeAt(0);
      const endRange = range.cloneRange();
      endRange.collapse(false);
      return endRange.getBoundingClientRect();
    };

    if (!selectedText || !selection?.rangeCount) {
      if (!e?.target?.closest?.(".text-selection-popup")) {
        setSelectionPopup({ visible: false, x: 0, y: 0, text: "" });
      }
      return;
    }

    const range = selection.getRangeAt(0);
    const anchorElement = selection.anchorNode?.parentElement;
    const focusElement = selection.focusNode?.parentElement;
    const startElement = range.startContainer?.parentElement;
    const endElement = range.endContainer?.parentElement;

    const isInsideSummaryModal =
      anchorElement?.closest(".RT-summary-modal-body") ||
      focusElement?.closest(".RT-summary-modal-body") ||
      startElement?.closest(".RT-summary-modal-body") ||
      endElement?.closest(".RT-summary-modal-body");

    if (!isInsideSummaryModal) {
      return;
    }

    const rect = getSelectionEndRect();
    if (!rect) {
      return;
    }

    const popupPoint = clampPopupToViewport(
      rect.left + rect.width / 2,
      rect.top - 12,
    );

    setSelectionPopup({
      visible: true,
      x: popupPoint.x,
      y: popupPoint.y,
      text: selectedText,
    });
  };

  const handleSaveSelectionToNotes = () => {
    if (!selectionPopup.text?.trim()) {
      return;
    }

    const selectedText = selectionPopup.text.trim();
    const noteDraft = createCurrentAffairsSelectionNoteDraft({
      title: buildSummaryTitle(),
      selectedText,
    });

    setEditNoteDraft(noteDraft);
    setShowEditNoteModal(true);
    setSelectionPopup({ visible: false, x: 0, y: 0, text: "" });
  };

  useEffect(() => {
    let cancelled = false;

    getMyNotesSubjects()
      .then((subjects) => {
        if (!cancelled) {
          setNoteSubjects(subjects);
        }
      })
      .catch((error) => {
        console.error("Failed to load note subjects", error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!showSummaryModal) {
      document.body.classList.remove("RT-summary-modal-open");
      setSelectionPopup({ visible: false, x: 0, y: 0, text: "" });
      return undefined;
    }

    document.body.classList.add("RT-summary-modal-open");

    return () => {
      document.body.classList.remove("RT-summary-modal-open");
    };
  }, [showSummaryModal]);

  useEffect(() => {
    if (!showSummaryModal) {
      return undefined;
    }

    const handleDocumentSelectionEnd = (event) => {
      handleModalTextSelect(event);
    };

    document.addEventListener("mouseup", handleDocumentSelectionEnd);
    return () => {
      document.removeEventListener("mouseup", handleDocumentSelectionEnd);
    };
  }, [showSummaryModal]);

  useEffect(() => {
    if (!showSummaryModal) {
      return undefined;
    }

    const handleSelectionKeyUp = (event) => {
      const navigationKeys = [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
        "PageUp",
        "PageDown",
      ];

      if (event.shiftKey && navigationKeys.includes(event.key)) {
        handleModalTextSelect();
      }
    };

    document.addEventListener("keyup", handleSelectionKeyUp);
    return () => {
      document.removeEventListener("keyup", handleSelectionKeyUp);
    };
  }, [showSummaryModal]);

  useEffect(() => {
    if (!selectionPopup.visible) {
      return undefined;
    }

    const handleClickOutside = (e) => {
      if (!e.target.closest(".text-selection-popup")) {
        setSelectionPopup({ visible: false, x: 0, y: 0, text: "" });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectionPopup.visible]);

  useEffect(() => {
    if (!showSummaryModal || !selectionPopup.visible) {
      return undefined;
    }

    const clearOnScroll = () => {
      setSelectionPopup({ visible: false, x: 0, y: 0, text: "" });
      window.getSelection()?.removeAllRanges();
    };

    document.addEventListener("scroll", clearOnScroll, true);
    return () => {
      document.removeEventListener("scroll", clearOnScroll, true);
    };
  }, [showSummaryModal, selectionPopup.visible]);

  const handelsend = (num, currentAffair) => {
 navigate(`/currentaffairs/${currentAffair.id}`);
  };

  const handleTestClick = (topic) => {
    setTopicKeyword(topic);
    if (onStartTest) {
      onStartTest(topic);
    }
  };

  const filteredData = relatedTopicsData
    ?.filter((item) => item.id !== id)
    ?.slice(0, 2);

  if (!showOnlySummaries && (!relatedTopicsData || relatedTopicsData.length === 0) && (!relatedSummariesData || relatedSummariesData.length === 0) && !syllabusProgressData && !relatedSummariesLoader && !syllabusProgressLoading && !showpracticetest) return null;
  if (showOnlySummaries && (!relatedSummariesData || relatedSummariesData.length === 0) && !syllabusProgressData && !relatedSummariesLoader && !syllabusProgressLoading && !showpracticetest) return null;

  return (
    <div className="RT-related-container">
      {/* Related Current Affairs Section - hidden in chat window */}
      {/* {!showOnlySummaries && (
        <div className="RT-topic-section">
          <div className="RT-topic-header">
            <img
              src="/assets/images/related-1.svg"
              alt="Arrow Icon"
              className="RT-topic-icon"
            />
            <span className="RT-topic-title">Related Current Affairs</span>
          </div>

          {!relatedLoader && filteredData?.map((item, index) => (
            <div
              key={index}
              className="RT-info-card"
              onClick={() => handelsend(item.id, item)}
            >
              <div>
                <p className="RT-main-heading RT-content-limit">{item.topic}</p>
              </div>
              {item.images[0] ? (
                <div className="RT-card-image-box">
                  <img
                    src={item.images[0]?.image_url.split("\n")[0]}
                    alt={item.topic}
                    className="RT-info-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/assets/images/noimage.jpeg";
                    }}
                  />
                </div>
              ) : (
                <div className="RT-card-image-box">
                  <img
                    src="/assets/images/noimage.jpeg"
                    alt={item.topic}
                    className="RT-info-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/assets/images/noimage.jpeg";
                    }}
                  />
                </div>
              )}
            </div>
          ))}
          {relatedLoader && (
            <div>
              <div>
                <Skeleton />
                <Skeleton animation="wave" />
                <Skeleton animation={false} />
              </div>
              <div>
                <Skeleton />
                <Skeleton animation="wave" />
                <Skeleton animation={false} />{" "}
              </div>
            </div>
          )}
        </div>
      )} */}

      {/* Current Affairs Summary Section - uses summaries API data, 1 card with title only */}
      {(relatedSummariesLoader || (relatedSummariesData && relatedSummariesData.length > 0)) && (
        <div className="RT-topic-section">
          <div className="RT-topic-header">
            <img
              src="/assets/images/related-1.svg"
              alt="Summary Icon"
              className="RT-topic-icon"
            />
            <span className="RT-topic-title">Current Affairs Summary</span>
          </div>

          {relatedSummariesLoader && (
            <div>
              <Skeleton />
              <Skeleton animation="wave" />
              <Skeleton animation={false} />
            </div>
          )}
          {!relatedSummariesLoader && relatedSummariesData?.slice(0, 3).length > 0 && (
            <div className="RT-info-card" onClick={() => setShowSummaryModal(true)} style={{ cursor: "pointer" }}>
              {consolidatedItem ? (
                (() => {
                  const preview = parseConsolidatedSummary(
                    consolidatedItem.summary || consolidatedItem.content,
                  );
                  const takeaways =
                    preview.sections.find((s) => s.type === "takeaways")?.items ||
                    [];
                  return (
                    <>
                      <p className="RT-summary-title-line">
                        {preview.title || consolidatedItem.title || consolidatedItem.topic}
                      </p>
                      {takeaways.slice(0, 2).map((point, index) => (
                        <p key={index} className="RT-summary-title-line">
                          {String(point).replace(/\*\*/g, "")}
                        </p>
                      ))}
                    </>
                  );
                })()
              ) : (
                relatedSummariesData.slice(0, 3).map((item, index) => (
                  <p key={index} className="RT-summary-title-line">{item.title || item.topic || item.name}</p>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Summary Modal */}
      {showSummaryModal &&
        createPortal(
          <div className="RT-modal-overlay" onClick={() => setShowSummaryModal(false)}>
            <div className="RT-summary-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="RT-summary-modal-header">
                <div className="RT-summary-modal-title-wrap">
                  {consolidatedItem ? (
                    <span className="RT-summary-modal-title RT-summary-modal-title--consolidated">
                      {parseConsolidatedSummary(
                        consolidatedItem.summary || consolidatedItem.content
                      ).title ||
                        consolidatedItem.title ||
                        consolidatedItem.topic ||
                        "Current Affairs Summary"}
                    </span>
                  ) : (
                    <>
                      <img
                        src="/assets/images/cf-summary.svg"
                        alt="Summary"
                        className="RT-summary-modal-title-icon"
                      />
                      <span className="RT-summary-modal-title">Current Affairs Summary</span>
                    </>
                  )}
                </div>
                <div className="RT-summary-modal-actions">
                  <button
                    type="button"
                    className="RT-summary-modal-action"
                    onClick={handleSaveSummaryToNotes}
                    title="Save to My Notes"
                    aria-label="Save to My Notes"
                  >
                    <img src="/assets/images/note-2.svg" alt="Save to My Notes" />
                  </button>
                  <button
                    type="button"
                    className="RT-summary-modal-action"
                    onClick={handleDownloadSummaryPdf}
                    title="Download"
                    aria-label="Download"
                  >
                    <img src="/assets/images/download.svg" alt="Download" />
                  </button>
                </div>
                <button
                  type="button"
                  className="RT-summary-modal-close"
                  onClick={() => setShowSummaryModal(false)}
                  aria-label="Close"
                >
                  <img src="/assets/images/close.svg" alt="Close" />
                </button>
              </div>
              <div className="RT-summary-modal-body">
                {consolidatedItem ? (
                  <ConsolidatedSummary
                    summary={consolidatedItem.summary || consolidatedItem.content}
                    articlesUsed={consolidatedItem.articles_used || []}
                    dateRange={consolidatedItem.date_range}
                    articleCount={consolidatedItem.article_count}
                    onArticleClick={handelsend}
                    hideTitle
                  />
                ) : (
                  relatedSummariesData.map((item, index) => (
                    <div key={index} className="RT-summary-item-card" onClick={() => handelsend(item.id, item)} style={{ cursor: "pointer" }}>
                      <div className="RT-summary-item-main">
                        <div className="RT-summary-item-topline">
                          <span className="RT-summary-item-title">{item.title || item.topic || item.name}</span>
                          <span className="RT-summary-item-arrow" aria-hidden="true">
                            <span className="RT-summary-item-date">{formatSummaryDate(item.ca_created_date)}</span>
                            <img src="/assets/images/arrow-navi.svg" alt="" />
                          </span>
                        </div>
                        <div className="RT-summary-item-points">
                          {extractBulletLines(item).map((point, bulletIndex) => (
                            <span key={bulletIndex} className="RT-summary-item-text">
                              {boldNumbers(point)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {showSummaryModal &&
        selectionPopup.visible &&
        createPortal(
          <div
            className="text-selection-popup"
            style={{
              position: "fixed",
              top: selectionPopup.y - 30,
              left: selectionPopup.x,
              transform: "translate(-50%, 0)",
              background: "white",
              borderRadius: "8px",
              boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
              zIndex: 1000001,
              display: "flex",
              gap: "8px",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              style={{
                outline: "none",
                border: "none",
                color: "black",
                borderRadius: "4px",
                cursor: "pointer",
                background: "linear-gradient(90deg, #D9F4FF 0%, #F4FCFF 100%)",
                border: "1px solid #B1E0F5",
              }}
              onClick={handleSaveSelectionToNotes}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <span>
                  <img
                    src="../assets/images/my_notes_new_sidebar_active.svg"
                    alt="Save Notes"
                    height={25}
                    width={25}
                  />
                </span>
                <p
                  style={{
                    margin: "0px",
                    marginTop: "2px",
                    minWidth: "80px",
                  }}
                >
                  Save Notes
                </p>
              </div>
            </button>
          </div>,
          document.body,
        )}

      {showEditNoteModal &&
        editNoteDraft &&
        createPortal(
          <div className="rt-note-modal-host">
            <EditNoteModal
              note={editNoteDraft}
              isSaving={isSavingNote}
              subjects={noteSubjects}
              onClose={handleEditNoteClose}
              onSave={handleEditNoteSave}
            />
          </div>,
          document.body,
        )}

      {noteToastMessage &&
        createPortal(
          <div className="rt-note-modal-host">
            <NoteToast
              message={noteToastMessage}
              variant={noteToastVariant}
              onClose={() => setNoteToastMessage("")}
            />
          </div>,
          document.body,
        )}

      {/* Syllabus Progress Section */}
      <SyllabusProgress data={syllabusProgressData} loading={syllabusProgressLoading} />

      {/* Practice Test Section */}
      {showpracticetest && (
        <div className="RT-topic-section">
          <div className="RT-topic-header">
            <img
              src="/assets/images/related-3.svg"
              alt="Arrow Icon"
              className="RT-topic-icon"
            />
            <span className="RT-topic-title">Prelims PYQs</span>
          </div>

          <div className="RT-quiz-section">
            <div className="RT-quiz-header">
              <p className="RT-quiz-title">Take a Practice Test</p>
              <img
                src="/assets/images/achievement.png"
                alt="achievement"
                className="RT-achievement-img"
              />
            </div>

            <div className="RT-quiz-button-container">
          {keywords.slice(0,5).map((item, index) => (
                <button
                  key={index}
                  className="RT-quiz-button"
                  onClick={() => handleTestClick(item)}
                >
                  {item} +
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// SyllabusProgress component: renders accordion + progress bars
const SyllabusProgress = ({ data, loading }) => {
  const [openSubjectIndex, setOpenSubjectIndex] = useState(0);
  const [openTopicIndexes, setOpenTopicIndexes] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (data) {
      setOpenSubjectIndex(0);
      setOpenTopicIndexes(() => {
        const firstOpenTopics = {};
        data?.subjects?.forEach((subject, index) => {
          firstOpenTopics[index] = Array.isArray(subject?.topics) && subject.topics.length > 0 ? 0 : null;
        });
        return firstOpenTopics;
      });
    }
  }, [data]);

  const getPriorityFromData = (d) => {
    if (!d) return null;
    if (d.priority) return d.priority;
    if (d.header_box) return d.header_box;
    if (d.critical_priority) return d.critical_priority;
    if (d.criticalPriority) {
      if (Array.isArray(d.criticalPriority)) {
        return { title: 'CRITICAL PRIORITY', items: d.criticalPriority };
      }
      return d.criticalPriority;
    }
    return null;
  };

  const priority = useMemo(() => data ? getPriorityFromData(data) : null, [data]);

  const randomPriorityItems = useMemo(
    () => (priority?.items ? priority.items.slice(0, 3) : null),
    [priority?.items],
  );

  const randomStructure = useMemo(() => {
    if (!data?.subjects) return [];
    const subjects = data.subjects.slice(0, 3);
    return subjects.map((s) => ({
      subject: s,
      originalSubjectIndex: data.subjects.indexOf(s),
      topics: (s.topics || []).slice(0, 3).map((t) => ({
        topic: t,
        originalTopicIndex: s.topics.indexOf(t),
        subtopics: (t.subtopics || []).slice(0, 3),
      })),
    }));
  }, [data?.subjects]);

  const parseProgress = (value) => {
    if (typeof value === "number") {
      return Math.max(0, Math.min(100, value));
    }

    const numericValue = Number.parseFloat(String(value || "").replace("%", ""));
    if (Number.isNaN(numericValue)) {
      return 0;
    }

    return Math.max(0, Math.min(100, numericValue));
  };

  const getProgressColor = (value) => {
    const progressValue = parseProgress(value);

    if (progressValue >= 100) {
      return "green";
    }

    if (progressValue <= 0) {
      return "empty";
    }

    return "blue";
  };

  if (loading) {
    return (
      <div className="RT-topic-section RT-syllabus-section">
        <div className="RT-topic-header">
          <img src="/assets/images/related-2.svg" alt="Syllabus" className="RT-topic-icon" />
          <span className="RT-topic-title">Syllabus Progress</span>
        </div>
        <div><Skeleton /><Skeleton animation="wave" /><Skeleton animation={false} /></div>
      </div>
    );
  }

  if (!data) return null;
  if ((!data.subjects || data.subjects.length === 0) && (!data.criticalPriority || data.criticalPriority.length === 0)) return null;

  const toggleSubject = (subjectIndex) => {
    setOpenSubjectIndex((prev) => (prev === subjectIndex ? null : subjectIndex));
  };

  const toggleTopic = (subjectIndex, topicIndex) => {
    setOpenTopicIndexes((prev) => ({
      ...prev,
      [subjectIndex]: prev[subjectIndex] === topicIndex ? null : topicIndex,
    }));
  };

  const handleVisitSyllabus = () => {
    const subjects = (data?.subjects || []).slice(0, 3).map((s) => ({
      name: s.name,
      topics: (s.topics || []).slice(0, 3).map((t) => t.name),
    }));
    navigate("/syllabus", {
      state: { syllabusSubjects: subjects },
    });
  };

  return (
    <div className="RT-topic-section RT-syllabus-section">
      <div className="RT-topic-header">
        <img src="/assets/images/related-2.svg" alt="Syllabus" className="RT-topic-icon" />
        <span className="RT-topic-title">Syllabus Progress</span>
      </div>

      {priority && (
        <div className="RT-syllabus-priority">
          <div className="RT-priority-accent" />
          <div className="RT-priority-content">
            <div className="RT-priority-title">
              <img src="/assets/images/warning.svg" alt="" className="RT-priority-icon" />
              <span className="RT-priority-heading">{priority.title || 'CRITICAL PRIORITY'}</span>
            </div>
            {Array.isArray(randomPriorityItems) && (
              <ul className="RT-priority-list">
                {randomPriorityItems.map((it, idx) => (
                  <li key={idx} className="RT-priority-item">{it}</li>
                ))}
              </ul>
            )}
            {typeof priority.note === 'string' && (
              <div className="RT-priority-note">{priority.note}</div>
            )}
          </div>
        </div>
      )}

      <div className="RT-syllabus-body">
        {randomStructure.map(({ subject, originalSubjectIndex, topics }) => (
          <div key={originalSubjectIndex} className="RT-syllabus-subject">
            <button
              type="button"
              className="RT-syllabus-row RT-syllabus-row-subject"
              onClick={() => toggleSubject(originalSubjectIndex)}
            >
              <div className="RT-syllabus-row-left">
                <span className={`RT-syllabus-chevron ${openSubjectIndex === originalSubjectIndex ? "open" : ""}`} aria-hidden="true">
                  <img src="/assets/images/open-slide.svg" alt="Open Slide" />
                </span>
                <span className="RT-syllabus-row-label">{subject.name}</span>
              </div>
              <span className="RT-syllabus-row-value">{subject.progress}</span>
            </button>
            <div className="RT-syllabus-progress-track">
              <div
                className={`RT-syllabus-progress-fill RT-syllabus-progress-${getProgressColor(subject.progress)}`}
                style={{ width: `${parseProgress(subject.progress)}%` }}
              />
            </div>

            {openSubjectIndex === originalSubjectIndex && topics.length > 0 && (
              <div className="RT-subject-topics">
                {topics.map(({ topic, originalTopicIndex, subtopics }) => (
                  <div key={originalTopicIndex} className="RT-syllabus-topic-block">
                    <button
                      type="button"
                      className="RT-syllabus-row RT-syllabus-row-topic"
                      onClick={() => toggleTopic(originalSubjectIndex, originalTopicIndex)}
                    >
                      <div className="RT-syllabus-row-left">
                        <span className={`RT-syllabus-chevron RT-syllabus-chevron-small ${openTopicIndexes[originalSubjectIndex] === originalTopicIndex ? "open" : ""}`} aria-hidden="true">
                          <img src="/assets/images/open-slide.svg" alt="Open Slide" />
                        </span>
                        <span className="RT-syllabus-row-label">{topic.name}</span>
                      </div>
                      <span className="RT-syllabus-row-value">{topic.progress}</span>
                    </button>
                    <div className="RT-syllabus-progress-track RT-syllabus-progress-track-topic">
                      <div
                        className={`RT-syllabus-progress-fill RT-syllabus-progress-${getProgressColor(topic.progress)}`}
                        style={{ width: `${parseProgress(topic.progress)}%` }}
                      />
                    </div>

                    {openTopicIndexes[originalSubjectIndex] === originalTopicIndex && subtopics.length > 0 && (
                      <div className="RT-syllabus-subtopics">
                        {subtopics.map((st, sidx) => (
                          <div key={sidx} className="RT-syllabus-subtopic">
                            <div className="RT-syllabus-row RT-syllabus-row-subtopic">
                              <span className="RT-syllabus-row-label">{st.name}</span>
                              <span className={`RT-syllabus-row-value RT-syllabus-row-value-${getProgressColor(st.progress)}`}>
                                {st.progress}
                              </span>
                            </div>
                            <div className="RT-syllabus-progress-track RT-syllabus-progress-track-subtopic">
                              <div
                                className={`RT-syllabus-progress-fill RT-syllabus-progress-${getProgressColor(st.progress)}`}
                                style={{ width: `${parseProgress(st.progress)}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        <button
          type="button"
          className="RT-syllabus-visit-link"
          onClick={handleVisitSyllabus}
        >
          <span className="RT-syllabus-visit-text">Visit Syllabus</span>
          <span className="RT-syllabus-visit-icon" aria-hidden="true">
            <img src="/assets/images/syllabus-visit-text.svg" alt="" />
          </span>
        </button>
      </div>
    </div>
  );
};

export default RelatedTopics;